import { useEffect, useRef } from 'react'
import { ChevronDown, ChevronRight } from '@/assets/icons'
import AgentResponseReferencesListItem from './AgentResponseReferencesListItem'
import { type AgentReferenceDisplayGroup } from '@/agent/references/util/agent-reference-footnotes'
import { getCaseStatusFromReference } from '@/agent/references/util/agent-reference-title'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CitatorSurface } from '@/analytics/enums/citator.enums'
import { trackCitatorReferenceGroupExpanded } from '@/analytics/utils/citator-analytics'

type AgentResponseReferencesGroupProps = {
  title: string
  numOfReferences: number
  referenceGroups: AgentReferenceDisplayGroup[]
  isExpanded: boolean
  toggleExpand: () => void
}

export default function AgentResponseReferencesGroup({ title, numOfReferences, referenceGroups, isExpanded, toggleExpand }: AgentResponseReferencesGroupProps) {
  const { trackEvent } = useAnalytics()
  const groupCitationIds = referenceGroups.flatMap((referenceGroup) => referenceGroup.citations.map((citation) => citation.citationId))

  // True if any reference in this group surfaces a treatment badge.
  const containsBadges = useAppSelector((state: RootState) =>
    groupCitationIds.some((citationId) => {
      const reference = state.agentReferencesState.references[citationId]
      return reference ? isDisplayableCaseStatus(getCaseStatusFromReference(reference)) : false
    })
  )

  // One impression per group expansion (first open only); the boolean flags badge exposure.
  const hasTrackedExpandRef = useRef(false)
  useEffect(() => {
    if (!isExpanded || hasTrackedExpandRef.current) return
    hasTrackedExpandRef.current = true
    trackCitatorReferenceGroupExpanded({ trackEvent, surface: CitatorSurface.Chat, containsBadges })
  }, [isExpanded, containsBadges, trackEvent])

  return (
    <div className="border-t pt-2">
      <div
        className={`px-1 py-2 text-sm text-brand-neutral-600 flex items-center gap-x-2 cursor-pointer rounded-md hover:bg-brand-neutral-50`}
        onClick={toggleExpand}
      >
        {numOfReferences} {title}
        <div>{isExpanded ? <ChevronDown width="16" height="16" /> : <ChevronRight width="16" height="16" />}</div>
      </div>
      {isExpanded && (
        <div className={`text-sm px-1 pb-3 flex flex-col gap-2`}>
          {referenceGroups.map((referenceGroup) => (
            <AgentResponseReferencesListItem key={`${title}_${referenceGroup.displayCitationId}`} referenceGroup={referenceGroup} />
          ))}
        </div>
      )}
    </div>
  )
}
